import styled from "styled-components";
import type {CSSProperties, ReactNode} from "react";
import {useEffect, useRef, useState} from "react";
import {colorPalette} from "../styles/colorPalette";
import {PointerDetectorReact} from "../../interaction/PointerDetectorReact";
import type {Pointer} from "../../../utils/interaction/Pointer";
import {MathUtils} from "../../../utils/math/MathUtils";
import {useAppStore} from "../../../StateManager";

interface IPanelProps {
	readonly children: ReactNode;
	readonly isOpen?: boolean;
	readonly style?: CSSProperties;
	readonly panelId?: string;
}

export const Panel = (props: IPanelProps) => {
	const {children, isOpen} = props;
	const key = "v5-spaceeditor-resizeable-panel-width";
	const id = props.panelId || "default";

	const resizerRef = useRef<HTMLDivElement>(null);
	const divRef = useRef<HTMLDivElement>(null);
	const timeoutId = useRef<number>(null);
	const frameRef = useRef<number | null>(null);

	const [isDragging, setIsDragging] = useState(false);

	const minWidth = 432;
	const maxWidth = Math.min(window.innerWidth / 2, 800);

	const panelWidth = useAppStore((state) => state.panelWidths[id] ?? minWidth);
	const setPanelWidth = useAppStore((state) => state.setPanelWidth);

	//load initial width from localStorage once
	useEffect(() => {
		const savedWidth = parseInt(localStorage.getItem(`panel-width-${id}`) || `${minWidth}`, 10);
		setPanelWidth(id, savedWidth);
	}, [setPanelWidth, id]);

	const onColResizeStart = () => {
		setIsDragging(true);
		localStorage.setItem(key, panelWidth.toString());
	};

	const onColResizeMove = (pointer: Pointer) => {
		if (frameRef.current) {
			cancelAnimationFrame(frameRef.current);
		}
		frameRef.current = requestAnimationFrame(() => {
			const newWidth = MathUtils.clamp(panelWidth - pointer.localX, minWidth, maxWidth);
			setPanelWidth(id, newWidth);
		});
	};

	const onColResizeEnd = () => {
		setIsDragging(false);
		if (frameRef.current) {
			cancelAnimationFrame(frameRef.current);
		}

		const finalWidth = useAppStore.getState().panelWidths[id] ?? minWidth;
		localStorage.setItem(`panel-width-${id}`, finalWidth.toString());
	};

	const style: CSSProperties = {
		transform: `translateX(${isOpen ? 0 : "100%"})`,
		width: `${panelWidth}px`,
		...props.style,
	};

	useEffect(() => {
		if (isOpen) {
			clearTimeout(timeoutId.current);
			timeoutId.current = window.setTimeout(() => {
				if (isOpen && divRef.current) {
					// workaround for drag n drop positioning issue. The dnd library doesn't work well if the parentelement has a css transform
					divRef.current.style.transform = "";
				}
			}, 200);
		}
	}, [isOpen]);

	return (
		<PanelStyled
			ref={divRef}
			style={style}
			className={props.panelId}
			$isDragging={isDragging}
		>
			<ResizerStyled ref={resizerRef} />
			<PointerDetectorReact
				onDown={onColResizeStart}
				onMove={onColResizeMove}
				onUp={onColResizeEnd}
				refObject={resizerRef}
			/>
			<OverflowAble>{children}</OverflowAble>
		</PanelStyled>
	);
};

const OverflowAble = styled.div`
	height: 100%;
	overflow-y: auto;
`;

const ResizerStyled = styled.div`
	width: 10px;
	position: absolute;
	height: 100%;
	left: 0;
	cursor: col-resize;
	border-left-color: ${colorPalette.gray.c300};
	border-left-width: 1px;
	border-left-style: solid;
`;

export const PanelStyled = styled.div<{$isDragging?: boolean}>`
	position: absolute;
	right: 0;
	top: 0;
	z-index: 10;
	background-color: ${colorPalette.white};
	transition: ${({$isDragging}) => ($isDragging ? "none" : "ease-in-out transform 0.2s")};
	height: calc(100vh - 49px); // Height was changed from 66px to 48px with the nav panel feature
	box-shadow: -4px 0px 12px 0px rgba(0, 0, 0, 0.2);
`;
